import React, { useEffect, useState, useCallback } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, ArrowUpRight, Sparkles, TrendingUp } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { track, trackLead } from "../../lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = "dc_exit_intent_v1";
const MOBILE_FALLBACK_MS = 45000; // 45s dwell on mobile

export default function ExitIntentModal() {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [goal, setGoal] = useState("Grow pipeline 2x");
  const [saving, setSaving] = useState(false);

  const alreadyShown = useCallback(() => {
    try {
      return sessionStorage.getItem(STORAGE_KEY) === "shown";
    } catch {
      return false;
    }
  }, []);

  const markShown = useCallback(() => {
    try {
      sessionStorage.setItem(STORAGE_KEY, "shown");
    } catch {
      /* no-op */
    }
  }, []);

  const trigger = useCallback(() => {
    if (alreadyShown()) return;
    markShown();
    setOpen(true);
    track("exit_intent_modal_shown");
  }, [alreadyShown, markShown]);

  useEffect(() => {
    if (alreadyShown()) return;

    const isTouch =
      typeof window !== "undefined" &&
      (window.matchMedia?.("(pointer: coarse)").matches ||
        "ontouchstart" in window);

    const onMouseOut = (e) => {
      // Only when the cursor leaves through the top of the viewport
      if (e.clientY <= 0 && !e.relatedTarget && !e.toElement) {
        trigger();
      }
    };

    let timer;
    if (isTouch) {
      timer = window.setTimeout(trigger, MOBILE_FALLBACK_MS);
    } else {
      document.addEventListener("mouseout", onMouseOut);
    }

    return () => {
      document.removeEventListener("mouseout", onMouseOut);
      if (timer) window.clearTimeout(timer);
    };
  }, [alreadyShown, trigger]);

  const close = useCallback(() => {
    setOpen(false);
    try {
      track("exit_intent_modal_dismissed");
    } catch {
      /* analytics should never block UX */
    }
  }, []);

  // ESC-to-close fallback. Some browsers/extensions intercept clicks on
  // small modal close buttons; keyboard close is a guaranteed exit path.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape" || e.key === "Esc") {
        e.preventDefault();
        close();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, close]);

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !/^\S+@\S+\.\S+$/.test(email)) {
      toast.error("Enter a valid work email.");
      return;
    }
    if (!name.trim()) {
      toast.error("Add your name so we can personalize it.");
      return;
    }
    setSaving(true);
    track("exit_intent_submit_attempt");
    try {
      await axios.post(`${API}/leads`, {
        type: "exit_intent",
        name,
        email,
        message: `Pipeline goal: ${goal}`,
        payload: { goal, source: "home_exit_intent" },
      });
      track("exit_intent_submit_success");
      trackLead("exit_intent", { goal });
      toast.success("Plan on the way. We'll ping you within one business day.");
      setOpen(false);
    } catch (err) {
      track("exit_intent_submit_error", { message: err?.message });
      toast.error("Something went wrong. Try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="exit-intent-modal"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6"
          role="dialog"
          aria-modal="true"
          aria-labelledby="exit-intent-title"
        >
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black/55 backdrop-blur-[6px]"
            onClick={close}
            data-testid="exit-intent-backdrop"
          />

          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.98 }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            className="relative z-10 w-full max-w-[560px] rounded-[28px] overflow-hidden bg-white border border-black/5 shadow-[0_40px_120px_-20px_rgba(0,0,0,0.35)]"
          >
            {/* Colorful aurora accent */}
            <div className="absolute -top-28 -right-28 w-[380px] h-[380px] rounded-full bg-[radial-gradient(circle,rgba(255,176,0,0.35),transparent_60%)] blur-3xl pointer-events-none" />
            <div className="absolute -bottom-28 -left-28 w-[320px] h-[320px] rounded-full bg-[radial-gradient(circle,rgba(59,167,209,0.28),transparent_60%)] blur-3xl pointer-events-none" />

            <button
              type="button"
              onClick={close}
              onMouseDown={(e) => e.stopPropagation()}
              data-testid="exit-intent-close"
              aria-label="Close"
              className="absolute top-3 right-3 z-30 w-12 h-12 rounded-full bg-black/5 hover:bg-black/15 active:bg-black/20 flex items-center justify-center transition-colors cursor-pointer"
            >
              {/* pointer-events-none on the SVG ensures the click always
                  lands on the <button> itself, never on an inner <path>
                  (some browser extensions break inner-SVG click bubbling). */}
              <X size={18} strokeWidth={2.2} className="pointer-events-none text-[#0A0A0A]" />
            </button>

            <div className="relative p-8 sm:p-10">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-gradient-to-r from-[#FFB000]/15 to-[#E96A7F]/15 border border-[#FFB000]/25 text-[11px] uppercase tracking-[0.22em] text-[#B87500] font-medium">
                <Sparkles size={12} strokeWidth={2} />
                Before you go
              </div>

              <h3
                id="exit-intent-title"
                className="mt-4 font-display text-[28px] sm:text-[34px] tracking-tighter leading-[1.05] font-semibold text-[#0A0A0A]"
              >
                Get your custom{" "}
                <span className="font-serif italic font-light bg-gradient-to-r from-[#FFB000] via-[#E96A7F] to-[#6B77E8] bg-clip-text text-transparent">
                  pipeline plan
                </span>
              </h3>
              <p className="mt-3 text-[15px] text-black/60 leading-relaxed">
                A 1-page directional plan: target accounts, MQL/SQL math, and
                the 3 plays we&apos;d run in your first 90 days. Free, no pitch.
              </p>

              <form onSubmit={submit} className="mt-6 space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Your name"
                    data-testid="exit-intent-name"
                    className="bg-black/[0.04] border border-black/10 rounded-full px-5 py-3.5 text-sm text-[#0A0A0A] placeholder:text-black/40 focus:border-[#FFB000] focus:bg-white outline-none transition-colors"
                  />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="work@email.com"
                    data-testid="exit-intent-email"
                    className="bg-black/[0.04] border border-black/10 rounded-full px-5 py-3.5 text-sm text-[#0A0A0A] placeholder:text-black/40 focus:border-[#FFB000] focus:bg-white outline-none transition-colors"
                  />
                </div>

                <div>
                  <div className="text-[10px] uppercase tracking-[0.22em] text-black/45 mb-2 pl-1">
                    Primary goal
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {[
                      "Grow pipeline 2x",
                      "Enter new market",
                      "Fix low MQL→SQL",
                      "Launch ABM",
                    ].map((g) => (
                      <button
                        key={g}
                        type="button"
                        onClick={() => setGoal(g)}
                        data-testid={`exit-intent-goal-${g.replace(/\s+/g, "-").toLowerCase()}`}
                        className={`px-4 py-2 rounded-full text-xs font-medium border transition-all ${
                          goal === g
                            ? "bg-[#0A0A0A] text-white border-[#0A0A0A]"
                            : "bg-white text-black/70 border-black/10 hover:border-black/30"
                        }`}
                      >
                        {g}
                      </button>
                    ))}
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={saving}
                  data-testid="exit-intent-submit"
                  className="btn-pill btn-primary-amber w-full text-[14px] py-4 justify-center disabled:opacity-60"
                >
                  {saving ? "Sending…" : "Send me my pipeline plan"}
                  <ArrowUpRight size={15} strokeWidth={2.2} />
                </button>
              </form>

              <div className="mt-5 flex items-center gap-2 text-[11px] text-black/45">
                <TrendingUp size={12} strokeWidth={2} />
                <span>
                  Trusted by Fortune 1000s · One business day response · No spam
                </span>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
